import Konva from 'konva';
import * as buttons from 'smg/buttons';
import { AddCartItemParamsUpload, AddCartItems } from 'smg/customer-api/carts';
import { HandleRequestError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import Modal from 'smg/modal';
import * as template from 'smg/template';
import * as uploads from 'smg/uploads';
import { Relref } from '../hugo';
import * as stockitems from '../stockitems';

export default async function main() {
    stockitems.InitGallery();
    stockitems.InitSeeMore();
    void stockitems.InitPrices();

    const $modalCanvasParent = template.Clone('#modal-canvas-parent-template').firstElementChild as HTMLElement;
    $modalCanvasParent.style.position = 'absolute';
    $modalCanvasParent.style.top = '-10000px';
    $modalCanvasParent.style.left = '-10000px';
    document.body.appendChild($modalCanvasParent);

    const $dropContainersParent = document.querySelector('#drop-containers-parent') as HTMLDivElement;

    $dropContainersParent.append(template.Clone('#drop-container-front-template'));
    $dropContainersParent.append(template.Clone('#drop-container-back-template'));

    const $form = document.querySelector('#stock-item-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;

    const $frontTopText = document.querySelector('#front-top-text') as HTMLTextAreaElement;
    const $frontBottomText = document.querySelector('#front-bottom-text') as HTMLTextAreaElement;
    const $backTopText = document.querySelector('#back-top-text') as HTMLTextAreaElement;
    const $backBottomText = document.querySelector('#back-bottom-text') as HTMLTextAreaElement;

    const $quantity = document.querySelector('#quantity') as HTMLInputElement;
    const $addToCart = document.querySelector('#add-to-cart') as HTMLButtonElement;
    const $personalize = document.querySelector('#personalize') as HTMLButtonElement;
    const $reopenEditor = document.querySelector('#reopen-editor-button-parent') as HTMLDivElement;

    const $modalDivider = document.querySelector('#modal-divider') as HTMLDivElement;

    const $canvasContainer = document.querySelector('#canvas-container') as HTMLDivElement;
    const $modalMainRow = document.querySelector('#modal-main-row') as HTMLDivElement;

    const $preview = document.querySelector('#preview-image-parent') as HTMLDivElement;

    const $fields = document.querySelector('#modal-right') as HTMLElement;
    const $frontInputs = document.querySelector('#front-inputs') as HTMLDivElement;
    const $backInputs = document.querySelector('#back-inputs') as HTMLDivElement;

    const $editorButtons = document.querySelector('#editor-buttons') as HTMLDivElement;

    const $navAlert = document.querySelector('#alert-nav-no-img') as HTMLDivElement;

    const $metaThemeColor = document.querySelector("meta[name='theme-color']") as HTMLMetaElement;

    const placeholderImg = `${Relref('/')}img/no-minimum-custom-coins/placeholder-image.png`;

    let modalActive = false;
    let backFace = false;
    let frontUploaded = false;
    let backUploaded = false;

    let editedTextFT = false;
    let editedTextFB = false;
    let editedTextBT = false;
    let editedTextBB = false;

    let firstOpen = true;

    const $canvasParent = document.querySelector('#canvas-parent') as HTMLDivElement;

    const $canvasUploaderLabelFront = document.querySelector('#canvas-uploader-front') as HTMLLabelElement;
    const $canvasUploaderLabelBack = document.querySelector('#canvas-uploader-back') as HTMLLabelElement;

    document.querySelector('#close-alert-nav')?.addEventListener('click', () => $navAlert.classList.add('d-none'));
    $quantity.parentElement?.classList.add('d-none');
    $addToCart.classList.add('d-none');

    // Define an interface for "editor state" to hold all data needed to reset the editor.
    /**
     * Holds all the data needed to recreate a state of the modal editor.
    */
    interface editorState {
        /** Front top text */
        textFT: string;
        /** Front bottom text */
        textFB: string;
        /** Back top text */
        textBT: string;
        /** Back bottom text */
        textBB: string;

        editedTextFT: boolean;
        editedTextFB: boolean;
        editedTextBT: boolean;
        editedTextBB: boolean;

        frontUploaded: boolean;
        backUploaded: boolean;

        imageFront: {
            /** Image src blob URL */
            ImageSrc: string;
            posX: number;
            posY: number;
            width: number;
            height: number;
            scaleX: number;
            scaleY: number;
            uploadID: number;
        };

        imageBack: {
            /** Image src blob URL */
            ImageSrc: string;
            posX: number;
            posY: number;
            width: number;
            height: number;
            scaleX: number;
            scaleY: number;
            uploadID: number;
        };
    }

    const stage = new Konva.Stage({
        container: $canvasParent,
    });

    const bgImage = await new Promise<Konva.Image>(resolve => {
        const img = new Image();
        img.onload = () =>
            resolve(new Konva.Image({
                image: img,
                draggable: false,
                listening: false,
            }));
        img.src = `${Relref('/')}img/no-minimum-custom-coins/antique brass bg-fs8.png`;
    });

    const bgLayer = new Konva.Layer();
    bgLayer.id('bgLayer');
    stage.add(bgLayer);
    bgLayer.add(bgImage);

    let scale = 1;
    let offsetX = 0;
    let offsetY = 0;

    function fitStageIntoParentContainer(firstRun = true) {
        const $stageChildren: ChildNode[] = [];
        if (!firstRun) {
            $stageChildren.push(...Array.from($canvasContainer.childNodes));
            $canvasContainer.innerHTML = '';
        }

        if (modalActive) {
            stage.width($canvasContainer.offsetWidth * (2000 / 968));
            stage.height(stage.width() * (968 / 2000));
            scale = (($canvasContainer.offsetWidth * (2000 / 968))) / bgImage.width();
        } else {
            stage.width(2000);
            stage.height(968); // = 969 + 64 + 969 = COIN + gutter + COIN
            scale = 1;
        }

        stage.scale({ x: scale, y: scale });

        if ($stageChildren) {
            for (const c of $stageChildren) {
                $canvasContainer.appendChild(c);
            }
        }

        offsetX = 0;
        offsetY = 0;

        if (modalActive) {
            if (backFace) {
                offsetX = 968 + 64;
            }
        }

        stage.offsetX(offsetX);
        stage.offsetY(-offsetY);

        $canvasContainer.setAttribute('style', `--scale:${scale};--offsetY:${offsetY}px;--offsetX:${offsetX}px`);
    }

    const $topTextTemplate = document.querySelector('#no-min-text-svg-top-template') as HTMLTemplateElement;
    const $bottomTextTemplate = document.querySelector('#no-min-text-svg-bottom-template') as HTMLTemplateElement;

    const $frontTopTextSVG = template.Clone($topTextTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $frontTopTextSVGText = $frontTopTextSVG.querySelector('#textPath114052') as SVGTextPathElement;
    $frontTopTextSVGText.textContent = 'FRONT TOP TEXT';

    //Functions to initialize the text previews to the correct phrases.
    //Choosing to do it this way instead of in the SVG itself because both
    //the front and the back use the same SVG file, and this is easier than
    //splitting it into four files.

    const getTextFrontTopSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$frontTopTextSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };
    const { img: frontTopImg, promise: frontTopPromise } = getTextFrontTopSVGImage();
    const textFrontTopImage = new Konva.Image({
        image: frontTopImg,
        width: 969,
        height: 969,
        x: 0,
        y: 0,
        id: 'textFrontTopImage',
    });
    const textFrontTopLayer = new Konva.Layer();
    textFrontTopLayer.id('textFrontTopLayer');
    textFrontTopLayer.add(textFrontTopImage);
    stage.add(textFrontTopLayer);

    async function updateFrontTopText(inputText: string) {
        $frontTopTextSVGText.textContent = inputText;
        const textFunc = getTextFrontTopSVGImage();
        await textFunc.promise;
        textFrontTopImage.image(textFunc.img);
    }

    $frontTopText.addEventListener('input', () => {
        editedTextFT = true;
        void updateFrontTopText($frontTopText.value);
    });

    const $frontBottomTextSVG = template.Clone($bottomTextTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $frontBottomTextSVGText = $frontBottomTextSVG.querySelector('#textPath168884') as SVGTextPathElement;
    $frontBottomTextSVGText.textContent = 'FRONT BOTTOM TEXT';

    const getTextFrontBottomSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$frontBottomTextSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };
    const { img: frontBottomImg, promise: frontBottomPromise } = getTextFrontBottomSVGImage();
    const textFrontBottomImage = new Konva.Image({
        image: frontBottomImg,
        width: 969,
        height: 969,
        x: 0,
        y: 0,
        id: 'textFrontBottomImage',
    });
    const textFrontBottomLayer = new Konva.Layer();
    textFrontBottomLayer.id('textFrontBottomLayer');
    textFrontBottomLayer.add(textFrontBottomImage);
    stage.add(textFrontBottomLayer);

    async function updateFrontBottomText(inputText: string) {
        $frontBottomTextSVGText.textContent = inputText;
        const textFunc = getTextFrontBottomSVGImage();
        await textFunc.promise;
        textFrontBottomImage.image(textFunc.img);
    }

    $frontBottomText.addEventListener('input', () => {
        editedTextFB = true;
        void updateFrontBottomText($frontBottomText.value);
    });

    const $backTopTextSVG = template.Clone($topTextTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $backTopTextSVGText = $backTopTextSVG.querySelector('#textPath114052') as SVGTextPathElement;
    $backTopTextSVGText.textContent = 'BACK TOP TEXT';

    const getTextBackTopSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$backTopTextSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };

    const { img: backTopImg, promise: backTopPromise } = getTextBackTopSVGImage();
    const textBackTopImage = new Konva.Image({
        image: backTopImg,
        width: 969,
        height: 969,
        x: 1033,
        y: 0,
        id: 'textBackTopImage',
    });
    const textBackTopLayer = new Konva.Layer();
    textBackTopLayer.id('textBackTopLayer');
    textBackTopLayer.add(textBackTopImage);
    stage.add(textBackTopLayer);

    async function updateBackTopText(inputText: string) {
        $backTopTextSVGText.textContent = inputText;
        const textFunc = getTextBackTopSVGImage();
        await textFunc.promise;
        textBackTopImage.image(textFunc.img);
    }

    $backTopText.addEventListener('input', () => {
        editedTextBT = true;
        void updateBackTopText($backTopText.value);
    });

    const $backBottomTextSVG = template.Clone($bottomTextTemplate).firstElementChild?.querySelector('svg') as SVGElement;
    const $backBottomTextSVGText = $backBottomTextSVG.querySelector('#textPath168884') as SVGTextPathElement;
    $backBottomTextSVGText.textContent = 'BACK BOTTOM TEXT';

    const getTextBackBottomSVGImage = () => {
        const img = new Image();
        const promise = new Promise<void>(resolve => img.addEventListener('load', () => resolve()));
        img.src = URL.createObjectURL(new Blob([$backBottomTextSVG.outerHTML], { type: 'image/svg+xml' }));
        return { img, promise };
    };

    const { img: backBottomImg, promise: backBottomPromise } = getTextBackBottomSVGImage();
    const textBackBottomImage = new Konva.Image({
        image: backBottomImg,
        width: 969,
        height: 969,
        x: 1033,
        y: 0,
        id: 'textBackBottomImage',
    });
    const textBackBottomLayer = new Konva.Layer();
    textBackBottomLayer.id('textBackBottomLayer');
    textBackBottomLayer.add(textBackBottomImage);
    stage.add(textBackBottomLayer);

    async function updateBackBottomText(inputText: string) {
        $backBottomTextSVGText.textContent = inputText;
        const textFunc = getTextBackBottomSVGImage();
        await textFunc.promise;
        textBackBottomImage.image(textFunc.img);
    }

    $backBottomText.addEventListener('input', () => {
        editedTextBB = true;
        void updateBackBottomText($backBottomText.value);
    });

    const $stageLoader = document.querySelector('#stage-loader') as HTMLElement;

    const showLoading = () => {
        $canvasContainer.style.opacity = '0.5';
        $canvasContainer.style.pointerEvents = 'none';
        $stageLoader.classList.remove('d-none');
    };

    const hideLoading = () => {
        $canvasContainer.style.opacity = '';
        $canvasContainer.style.pointerEvents = '';
        $stageLoader.classList.add('d-none');
    };

    // start front uploader

    let frontUploadID = 0;
    const frontUploadLayer = new Konva.Layer({
        x: bgImage.x(),
        y: bgImage.y(),
        width: bgImage.width(),
        height: bgImage.height(),
        id: 'frontUploadLayer',
    });
    const frontUploadImage = new Konva.Image({
        image: new Image(),
        id: 'frontUploadImage',
    });
    stage.add(frontUploadLayer);

    frontUploadLayer.add(frontUploadImage);

    const frontUploadTransformerLayer = new Konva.Layer({
        x: bgImage.x(),
        y: bgImage.y(),
        visible: false,
        id: 'frontUploadTransformerLayer',
    });
    const frontUploaderTransformer = new Konva.Transformer({
        anchorStroke: '#fff',
        borderStroke: '#1a73e8',
        anchorSize: 9,
        anchorFill: '#1a73e8',
        centeredScaling: true,
    });
    frontUploadTransformerLayer.add(frontUploaderTransformer);
    stage.add(frontUploadTransformerLayer);

    const frontTransformerShape = new Konva.Rect({
        draggable: true,
    });
    frontUploadTransformerLayer.add(frontTransformerShape);
    frontUploaderTransformer.nodes([frontTransformerShape]);

    const $dropContainerFront = document.querySelector('#drop-container-front') as HTMLElement;
    const $stageUploadButtonFront = document.querySelector('#stage-upload-button-front') as HTMLButtonElement;
    $stageUploadButtonFront.addEventListener('click', () => $canvasUploaderLabelFront.click());

    const $resetFront = document.querySelector('#reset-front') as HTMLButtonElement;
    const $editFront = document.querySelector('#edit-front') as HTMLButtonElement;
    const $changeImageFront = document.querySelector('#change-image-front') as HTMLButtonElement;
    const $doneFront = document.querySelector('#done-front') as HTMLButtonElement;

    const startTransformModeFront = () => {
        $editorButtons.classList.remove('d-none');
        $editFront.classList.add('d-none');
        $resetFront.classList.remove('d-none');
        $changeImageFront.classList.remove('d-none');
        $doneFront.classList.remove('d-none');

        $dropContainersParent.classList.add('pe-none');

        frontUploadTransformerLayer.show();
    };

    const stopTransformModeFront = () => {
        $resetFront.classList.add('d-none');
        $changeImageFront.classList.add('d-none');
        $doneFront.classList.add('d-none');

        $dropContainersParent.classList.remove('pe-none');

        if (!backFace && frontUploaded) {
            $editFront.classList.remove('d-none');
        }

        frontUploadTransformerLayer.hide();
    };

    let setFrontImageSizeAndPosition: () => void;

    $resetFront.addEventListener('click', e => {
        e.preventDefault();
        setFrontImageSizeAndPosition?.();
    });

    $editFront.addEventListener('click', startTransformModeFront);
    $changeImageFront.addEventListener('click', () => $canvasUploaderLabelFront.click());
    $doneFront.addEventListener('click', stopTransformModeFront);

    let frontImg = new Image();
    let frontShape: Konva.Shape;

    const addImageFront = (blobURL: string) => {
        const maskActualWidth = 645;
        const maskActualHeight = 645;
        const maskOffsetX = 162;
        const maskOffsetY = 162;
        frontImg = new Image();

        const mask = new Image();
        const promise = new Promise<void>(resolve => {
            if (blobURL === '') {
                if (frontShape) {
                    frontShape.destroy();
                }
                $dropContainerFront.classList.remove('d-none');
                resolve();
                return;
            }
            mask.onload = () => {
                frontImg.src = blobURL;
                frontImg.setAttribute('style', 'image-orientation:none');
                frontImg.onload = () => {
                    const originalRatios = {
                        width: maskActualWidth / frontImg.naturalWidth,
                        height: maskActualHeight / frontImg.naturalHeight,
                    };

                    // formula for cover:
                    const coverRatio = Math.max(originalRatios.width, originalRatios.height);

                    // result:
                    const newImageWidth = frontImg.naturalWidth * coverRatio;
                    const newImageHeight = frontImg.naturalHeight * coverRatio;

                    const newImageX = maskOffsetX - (newImageWidth - maskActualWidth) / 2;
                    const newImageY = maskOffsetY - (newImageHeight - maskActualHeight) / 2;

                    frontTransformerShape.setAttrs({
                        x: newImageX,
                        y: newImageY,
                        width: newImageWidth,
                        height: newImageHeight,
                        scaleX: 1,
                        scaleY: 1,
                    });
                    frontTransformerShape.draw();

                    if (frontShape) {
                        frontShape.destroy();
                    }

                    frontShape = new Konva.Shape({
                        sceneFunc: ctx => {
                            ctx.save();
                            ctx.drawImage(mask, -frontShape.x(), -frontShape.y(), mask.naturalWidth, mask.naturalHeight);
                            // TODO typescript version bug?
                            (ctx as unknown as Record<string, string>)['globalCompositeOperation'] = 'source-in';

                            const toRadians = Math.PI / 180;
                            const angleInDeg = frontUploaderTransformer.rotation();
                            ctx.rotate(angleInDeg * toRadians);
                            ctx.drawImage(frontImg, 0, 0, frontUploaderTransformer.width() / stage.scaleX(), frontUploaderTransformer.height() / stage.scaleX());
                            ctx.rotate(-angleInDeg * toRadians);

                            (ctx as unknown as Record<string, string>)['globalCompositeOperation'] = 'source-over';
                            ctx.restore();
                        },
                        hitFunc: ctx => {
                            ctx.rect(0, 0, frontUploaderTransformer.width() / stage.scaleX(), frontUploaderTransformer.height() / stage.scaleX());
                            ctx.fillStrokeShape(frontShape);
                        },
                    });
                    setFrontImageSizeAndPosition = () => {
                        frontTransformerShape.rotation(0);
                        frontShape.setAttrs({
                            x: newImageX,
                            y: newImageY,
                            width: newImageWidth,
                            height: newImageHeight,
                            scaleX: 1,
                            scaleY: 1,
                        });

                        frontTransformerShape.setAttrs({
                            x: newImageX,
                            y: newImageY,
                            width: newImageWidth,
                            height: newImageHeight,
                            scaleX: 1,
                            scaleY: 1,
                        });

                        frontTransformerShape.draw();
                    };
                    setFrontImageSizeAndPosition();
                    frontUploadLayer.add(frontShape);

                    frontTransformerShape.off('dragmove');
                    frontTransformerShape.on('dragmove', () => frontShape.position(frontTransformerShape.position()));

                    frontTransformerShape.off('transform');
                    frontTransformerShape.on('transform', () => frontShape.position(frontTransformerShape.position()));

                    frontShape.on('click', () => startTransformModeFront);

                    hideLoading();
                    resolve();
                };

                $dropContainerFront.classList.add('d-none');
                startTransformModeFront();

                $uploadRequired.forEach($el => $el.classList.add('d-none'));
            };
        });
        mask.src = `${Relref('/')}img/no-minimum-custom-coins/front-mask.png`;
        $canvasContainer.classList.remove('no-edit-btn');
        return promise;
    };

    new uploads.Uploader($canvasUploaderLabelFront, {
        dropZones: [$dropContainerFront],
        start: showLoading,
        stop: hideLoading,
        success: async (thumb, uploadID, blobURL) => {
            $navAlert.classList.add('d-none');
            frontUploadID = uploadID;
            frontUploaded = true;
            await addImageFront(blobURL);
        },
    });

    // stop front uploader

    // start back uploader

    let backUploadID = 0;
    const backUploadLayer = new Konva.Layer({
        x: bgImage.x(),
        y: bgImage.y(),
        width: bgImage.width(),
        height: bgImage.height(),
        id: 'backUploadLayer',
    });
    const backUploadImage = new Konva.Image({
        image: new Image(),
        id: 'backUploadImage',
    });
    stage.add(backUploadLayer);

    backUploadLayer.add(backUploadImage);

    const backUploadTransformerLayer = new Konva.Layer({
        x: bgImage.x(),
        y: bgImage.y(),
        visible: false,
        id: 'backUploadTransformerLayer',
    });
    const backUploaderTransformer = new Konva.Transformer({
        anchorStroke: '#fff',
        borderStroke: '#1a73e8',
        anchorSize: 9,
        anchorFill: '#1a73e8',
        centeredScaling: true,
    });
    backUploadTransformerLayer.add(backUploaderTransformer);
    stage.add(backUploadTransformerLayer);

    const backTransformerShape = new Konva.Rect({
        draggable: true,
    });
    backUploadTransformerLayer.add(backTransformerShape);
    backUploaderTransformer.nodes([backTransformerShape]);

    const $dropContainerBack = document.querySelector('#drop-container-back') as HTMLElement;
    const $stageUploadButtonBack = document.querySelector('#stage-upload-button-back') as HTMLButtonElement;
    $stageUploadButtonBack.addEventListener('click', () => $canvasUploaderLabelBack.click());

    const $resetBack = document.querySelector('#reset-back') as HTMLButtonElement;
    const $editBack = document.querySelector('#edit-back') as HTMLButtonElement;
    const $changeImageBack = document.querySelector('#change-image-back') as HTMLButtonElement;
    const $doneBack = document.querySelector('#done-back') as HTMLButtonElement;

    const startTransformModeBack = () => {
        $editorButtons.classList.remove('d-none');
        $editBack.classList.add('d-none');
        $resetBack.classList.remove('d-none');
        $changeImageBack.classList.remove('d-none');
        $doneBack.classList.remove('d-none');

        $dropContainersParent.classList.add('pe-none');

        backUploadTransformerLayer.show();
    };

    const stopTransformModeBack = () => {
        $resetBack.classList.add('d-none');
        $changeImageBack.classList.add('d-none');
        $doneBack.classList.add('d-none');

        $dropContainersParent.classList.remove('pe-none');

        if (backFace && backUploaded) {
            $editBack.classList.remove('d-none');
        }

        backUploadTransformerLayer.hide();
    };

    let setBackImageSizeAndPosition: () => void;

    $resetBack.addEventListener('click', e => {
        e.preventDefault();
        setBackImageSizeAndPosition?.();
    });

    $editBack.addEventListener('click', startTransformModeBack);
    $changeImageBack.addEventListener('click', () => $canvasUploaderLabelBack.click());
    $doneBack.addEventListener('click', stopTransformModeBack);

    let backImg = new Image();
    let backShape: Konva.Shape;

    const addImageBack = (blobURL: string) => {
        const maskActualWidth = 645;
        const maskActualHeight = 645;
        const maskOffsetX = 1195;
        const maskOffsetY = 162;
        backImg = new Image();

        const mask = new Image();
        const promise = new Promise<void>(resolve => {
            if (blobURL === '') {
                if (backShape) {
                    backShape.destroy();
                }
                $dropContainerBack.classList.remove('d-none');
                resolve();
                return;
            }
            mask.onload = () => {
                backImg.onload = () => {
                    const originalRatios = {
                        width: maskActualWidth / backImg.naturalWidth,
                        height: maskActualHeight / backImg.naturalHeight,
                    };

                    // formula for cover:
                    const coverRatio = Math.max(originalRatios.width, originalRatios.height);

                    // result:
                    const newImageWidth = backImg.naturalWidth * coverRatio;
                    const newImageHeight = backImg.naturalHeight * coverRatio;

                    const newImageX = maskOffsetX - (newImageWidth - maskActualWidth) / 2;
                    const newImageY = maskOffsetY - (newImageHeight - maskActualHeight) / 2;

                    backTransformerShape.setAttrs({
                        x: newImageX,
                        y: newImageY,
                        width: newImageWidth,
                        height: newImageHeight,
                        scaleX: 1,
                        scaleY: 1,
                    });
                    backTransformerShape.draw();

                    if (backShape) {
                        backShape.destroy();
                    }
                    backShape = new Konva.Shape({
                        sceneFunc: ctx => {
                            ctx.save();
                            ctx.drawImage(mask, -backShape.x(), -backShape.y(), mask.naturalWidth, mask.naturalHeight);
                            // TODO typescript version bug?
                            (ctx as unknown as Record<string, string>)['globalCompositeOperation'] = 'source-in';

                            const toRadians = Math.PI / 180;
                            const angleInDeg = backUploaderTransformer.rotation();
                            ctx.rotate(angleInDeg * toRadians);
                            ctx.drawImage(backImg, 0, 0, backUploaderTransformer.width() / stage.scaleX(), backUploaderTransformer.height() / stage.scaleX());
                            ctx.rotate(-angleInDeg * toRadians);

                            (ctx as unknown as Record<string, string>)['globalCompositeOperation'] = 'source-over';
                            ctx.restore();
                        },
                        hitFunc: ctx => {
                            ctx.rect(0, 0, backUploaderTransformer.width() / stage.scaleX(), backUploaderTransformer.height() / stage.scaleX());
                            ctx.fillStrokeShape(backShape);
                        },
                    });
                    setBackImageSizeAndPosition = () => {
                        backTransformerShape.rotation(0);
                        backShape.setAttrs({
                            x: newImageX,
                            y: newImageY,
                            width: newImageWidth,
                            height: newImageHeight,
                            scaleX: 1,
                            scaleY: 1,
                        });

                        backTransformerShape.setAttrs({
                            x: newImageX,
                            y: newImageY,
                            width: newImageWidth,
                            height: newImageHeight,
                            scaleX: 1,
                            scaleY: 1,
                        });

                        backTransformerShape.draw();
                    };
                    setBackImageSizeAndPosition();
                    backUploadLayer.add(backShape);

                    backTransformerShape.off('dragmove');
                    backTransformerShape.on('dragmove', () => backShape.position(backTransformerShape.position()));

                    backTransformerShape.off('transform');
                    backTransformerShape.on('transform', () => backShape.position(backTransformerShape.position()));

                    backShape.on('click', () => startTransformModeBack);

                    hideLoading();
                    resolve();
                };
                backImg.src = blobURL;

                $dropContainerBack.classList.add('d-none');
                startTransformModeBack();

                $uploadRequired.forEach($el => $el.classList.add('d-none'));
            };
        });
        mask.src = `${Relref('/')}img/no-minimum-custom-coins/back-mask.png`;
        $canvasContainer.classList.remove('no-edit-btn');
        return promise;
    };

    new uploads.Uploader($canvasUploaderLabelBack, {
        dropZones: [$dropContainerBack],
        start: showLoading,
        stop: hideLoading,
        success: async (thumb, uploadID, blobURL) => {
            $navAlert.classList.add('d-none');
            backUploadID = uploadID;
            backUploaded = true;
            await addImageBack(blobURL);
        },
    });

    // stop back uploader

    // Disable hit graph for most Konva layers, should improve performance by disabling mouse event detection.
    bgLayer.hitGraphEnabled(false);
    textFrontTopLayer.hitGraphEnabled(false);
    textFrontBottomLayer.hitGraphEnabled(false);
    textBackTopLayer.hitGraphEnabled(false);
    textBackBottomLayer.hitGraphEnabled(false);

    let frontPreviewImgBlob: string;
    let backPreviewImgBlob: string;

    function createCoinPreview() {
        $preview.innerHTML = '';
        $preview.append(template.Clone('#loading'));

        // crop and generate preview images
        scale = 1;
        const $frontPreviewImg = new Image();
        frontPreviewImgBlob = stage.toDataURL({
            quality: 1,
            mimeType: 'image/png',
            x: offsetX * scale,
            y: offsetY * scale,
            width: 969 * scale,
            height: 969 * scale,
        });
        $frontPreviewImg.src = frontPreviewImgBlob;

        const $backPreviewImg = new Image();
        backPreviewImgBlob = stage.toDataURL({
            quality: 1,
            mimeType: 'image/png',
            x: (offsetX + 969 + 64) * scale,
            y: offsetY * scale,
            width: 969 * scale,
            height: 969 * scale,
        });
        $backPreviewImg.src = backPreviewImgBlob;

        $frontPreviewImg.setAttribute('id', 'front-preview-image');
        $backPreviewImg.setAttribute('id', 'back-preview-image');

        $preview.append($frontPreviewImg);
        $preview.append($backPreviewImg);

        $preview.querySelector('#preview-loading-icon')?.remove();
    }

    $canvasParent.firstElementChild?.setAttribute('id', 'editor');

    const $uploadRequired = document.querySelectorAll('.upload-required');

    document.addEventListener('click', e => {
        if (!frontUploadID && !backUploadID) {
            return;
        }

        if (e.target !== $canvasContainer
            && e.target !== $editFront
            && e.target !== $editBack
            && e.target !== $dropContainersParent
            && !(e.target instanceof HTMLElement && e.target?.closest('#canvas-container'))) {
            stopTransformModeFront();
            stopTransformModeBack();
        }
    });

    let prev: editorState = {
        textFT: $frontTopText.value ?? '',
        textFB: $frontBottomText.value ?? '',
        textBT: $backTopText.value ?? '',
        textBB: $backBottomText.value ?? '',
        editedTextFT: editedTextFT,
        editedTextFB: editedTextFB,
        editedTextBT: editedTextBT,
        editedTextBB: editedTextBB,
        frontUploaded: frontUploaded,
        backUploaded: backUploaded,
        imageFront: {
            ImageSrc: placeholderImg,
            posX: frontTransformerShape.x(),
            posY: frontTransformerShape.y(),
            width: frontTransformerShape.width(),
            height: frontTransformerShape.height(),
            scaleX: frontTransformerShape.scaleX(),
            scaleY: frontTransformerShape.scaleY(),
            uploadID: frontUploadID ?? 0,
        },
        imageBack: {
            ImageSrc: placeholderImg,
            posX: backTransformerShape.x(),
            posY: backTransformerShape.y(),
            width: backTransformerShape.width(),
            height: backTransformerShape.height(),
            scaleX: backTransformerShape.scaleX(),
            scaleY: backTransformerShape.scaleY(),
            uploadID: backUploadID ?? 0,
        },
    };

    function modalEditor() {
        $fields?.classList.remove('d-none');
        $editorButtons?.classList.remove('d-none');
        $modalDivider?.classList.add('d-lg-flex');
        $reopenEditor.classList.add('d-none');

        modalActive = true;
        const prevFirstOpen = firstOpen;

        prev = {
            textFT: $frontTopText.value ?? '',
            textFB: $frontBottomText.value ?? '',
            textBT: $backTopText.value ?? '',
            textBB: $backBottomText.value ?? '',
            editedTextFT: editedTextFT,
            editedTextFB: editedTextFB,
            editedTextBT: editedTextBT,
            editedTextBB: editedTextBB,
            frontUploaded: frontUploaded,
            backUploaded: backUploaded,
            imageFront: {
                ImageSrc: '',
                posX: frontTransformerShape.x(),
                posY: frontTransformerShape.y(),
                width: frontTransformerShape.width(),
                height: frontTransformerShape.height(),
                scaleX: frontTransformerShape.scaleX(),
                scaleY: frontTransformerShape.scaleY(),
                uploadID: frontUploadID ?? 0,
            },
            imageBack: {
                ImageSrc: '',
                posX: backTransformerShape.x(),
                posY: backTransformerShape.y(),
                width: backTransformerShape.width(),
                height: backTransformerShape.height(),
                scaleX: backTransformerShape.scaleX(),
                scaleY: backTransformerShape.scaleY(),
                uploadID: backUploadID ?? 0,
            },
        };

        prev.imageFront.ImageSrc = frontImg.src;
        prev.imageBack.ImageSrc = backImg.src;

        if (firstOpen) {
            void updateFrontTopText('');
            void updateFrontBottomText('');
            void updateBackTopText('');
            void updateBackBottomText('');

            void addImageFront('').then(stopTransformModeFront);
            void addImageBack('').then(stopTransformModeBack);
        }

        firstOpen = false;

        function modalToFront() {
            $modalToFrontSM.setAttribute('style', 'opacity:0');
            $modalToFrontSM.setAttribute('disabled', '');

            $navAlert.classList.add('d-none');
            backFace = false;

            stopTransformModeBack();

            modalTitle.textContent = 'Front of Coin';

            $frontInputs?.classList.remove('d-none');
            $backInputs?.classList.add('d-none');

            $editBack?.classList.add('d-none');
            $editFront?.classList.add('d-none');

            fitStageIntoParentContainer(false);

            $dropContainerBack?.classList.add('d-none');
            if (!frontUploaded) {
                $dropContainerFront?.classList.remove('d-none');
                $canvasContainer.classList.add('no-edit-btn');
                $editorButtons.classList.add('d-none');
            } else {
                $editorButtons.classList.remove('d-none');
                $editFront?.classList.remove('d-none');
                $canvasContainer.classList.remove('no-edit-btn');
            }
        }
        function modalToBack() {
            $modalToFrontSM.removeAttribute('style');
            $modalToFrontSM.removeAttribute('disabled');

            $navAlert.classList.add('d-none');
            backFace = true;

            stopTransformModeFront();

            modalTitle.textContent = 'Back of Coin';

            $frontInputs?.classList.add('d-none');
            $backInputs?.classList.remove('d-none');

            $editFront?.classList.add('d-none');
            $editBack?.classList.add('d-none');

            fitStageIntoParentContainer(false);

            $dropContainerFront?.classList.add('d-none');
            if (!backUploaded) {
                $dropContainerBack?.classList.remove('d-none');
                $canvasContainer.classList.add('no-edit-btn');
                $editorButtons.classList.add('d-none');
            } else {
                $editorButtons.classList.remove('d-none');
                $editBack?.classList.remove('d-none');
                $canvasContainer.classList.remove('no-edit-btn');
            }
        }

        const modalBtnToFront = () => {
            activeModal.Element.querySelector('.btn-modal-to-back')?.classList.remove('d-none');
            activeModal.Element.querySelector('.btn-modal-to-front')?.classList.add('d-none');
            activeModal.Element.querySelector('.btn-modal-complete')?.classList.add('d-none');
            modalToFront();
        };

        const modalBtnToBack = () => {
            if (!frontUploadID) {
                $navAlert.classList.remove('d-none');
                return;
            }
            activeModal.Element.querySelector('.btn-modal-to-back')?.classList.add('d-none');
            activeModal.Element.querySelector('.btn-modal-to-front')?.classList.remove('d-none');
            activeModal.Element.querySelector('.btn-modal-complete')?.classList.remove('d-none');
            modalToBack();
        };

        if (!frontUploaded) {
            $dropContainerFront?.classList.remove('d-none');
        }

        $dropContainerBack?.classList.remove('d-none');

        for (const $el of Array.from($uploadRequired)) {
            $el.classList.add('d-none');
        }

        $modalMainRow.classList.remove('d-none');
        $preview?.classList.add('d-none');

        $modalCanvasParent.removeAttribute('style');

        const exitModal = async (cancel?: boolean) => {
            stopTransformModeFront();
            stopTransformModeBack();

            $metaThemeColor.content = '#00b7ae';

            $editFront?.classList.add('d-none');
            $editBack?.classList.add('d-none');

            $fields?.classList.add('d-none');
            $editorButtons?.classList.add('d-none');
            $dropContainerFront?.classList.add('d-none');
            $dropContainerBack?.classList.add('d-none');
            $modalDivider?.classList.remove('d-lg-flex');

            modalActive = false;

            $modalCanvasParent.style.position = 'absolute';
            $modalCanvasParent.style.top = '-10000px';
            $modalCanvasParent.style.left = '-10000px';

            fitStageIntoParentContainer(false);

            if (!editedTextFT) {
                await updateFrontTopText('');
            }
            if (!editedTextFB) {
                await updateFrontBottomText('');
            }
            if (!editedTextBT) {
                await updateBackTopText('');
            }
            if (!editedTextBB) {
                await updateBackBottomText('');
            }

            $preview?.classList.remove('d-none');

            if (cancel) {
                await resetToPrev().then(() => {
                    createCoinPreview();
                    firstOpen = prevFirstOpen;
                });
            } else {
                createCoinPreview();
                firstOpen = false;
            }

            if (!firstOpen) {
                $personalize.classList.add('d-none');
                $addToCart.classList.remove('d-none');
                $quantity.parentElement?.classList.remove('d-none');
                $reopenEditor.classList.remove('d-none');
            }

            $modalMainRow.classList.add('d-none');
            activeModal.Close();
        };

        async function resetToPrev() {
            // Reset front image
            if (prevFirstOpen) {
                await addImageFront(placeholderImg).then(stopTransformModeFront);
                await addImageBack(placeholderImg).then(stopTransformModeBack);
            } else {
                await addImageFront(prev.imageFront.ImageSrc).then(() => {
                    stopTransformModeFront();
                    if (frontShape) {
                        frontShape.setAttrs({
                            x: prev.imageFront.posX,
                            y: prev.imageFront.posY,
                        });
                        frontTransformerShape.setAttrs({
                            x: prev.imageFront.posX,
                            y: prev.imageFront.posY,
                            width: prev.imageFront.width,
                            height: prev.imageFront.height,
                            scaleX: prev.imageFront.scaleX,
                            scaleY: prev.imageFront.scaleY,
                        });
                    }
                }).catch();
                frontUploaded = prev.frontUploaded;

                // Reset back image
                await addImageBack(prev.imageBack.ImageSrc).then(() => {
                    stopTransformModeBack();
                    if (backShape) {
                        backShape.setAttrs({
                            x: prev.imageBack.posX,
                            y: prev.imageBack.posY,
                        });
                        backTransformerShape.setAttrs({
                            x: prev.imageBack.posX,
                            y: prev.imageBack.posY,
                            width: prev.imageBack.width,
                            height: prev.imageBack.height,
                            scaleX: prev.imageBack.scaleX,
                            scaleY: prev.imageBack.scaleY,
                        });
                    }
                }).catch();
                backUploaded = prev.backUploaded;
            }
            // Reset Text
            let resetTextFT: string;
            let resetTextFB: string;
            let resetTextBT: string;
            let resetTextBB: string;

            if (prevFirstOpen) {
                resetTextFT = 'FRONT TOP TEXT';
                resetTextFB = 'FRONT BOTTOM TEXT';
                resetTextBT = 'BACK TOP TEXT';
                resetTextBB = 'BACK BOTTOM TEXT';
            } else {
                if (prev.editedTextFT) {
                    resetTextFT = prev.textFT;
                } else {
                    resetTextFT = '';
                }
                if (prev.editedTextFB) {
                    resetTextFB = prev.textFB;
                } else {
                    resetTextFB = '';
                }
                if (prev.editedTextBT) {
                    resetTextBT = prev.textBT;
                } else {
                    resetTextBT = '';
                }
                if (prev.editedTextBB) {
                    resetTextBB = prev.textBB;
                } else {
                    resetTextBB = '';
                }
            }
            await updateFrontTopText(resetTextFT);
            await updateFrontBottomText(resetTextFB);
            await updateBackTopText(resetTextBT);
            await updateBackBottomText(resetTextBB);
            $frontTopText.value = prev.textFT;
            $frontBottomText.value = prev.textFB;
            $backTopText.value = prev.textBT;
            $backBottomText.value = prev.textBB;

            editedTextFT = prev.editedTextFT;
            editedTextFB = prev.editedTextFB;
            editedTextBT = prev.editedTextBT;
            editedTextBB = prev.editedTextBB;

            // Reset upload IDs
            frontUploadID = prev.imageFront.uploadID;
            backUploadID = prev.imageBack.uploadID;

            // Should now be fully reset
        }

        $metaThemeColor.content = '#ffffff';

        const activeModal = new Modal({
            title: 'Front of Coin',
            size: 'modal-lg',
            body: $modalCanvasParent,
            outsideClosable: false,
            submitAutoFocus: false,
            buttons: [
                {
                    text: 'Complete Coin Design',
                    class: 'btn btn-secondary text-white btn-modal-complete d-none',
                    click: async () => {
                        if (!frontUploadID || !backUploadID) {
                            $navAlert.classList.remove('d-none');
                            return;
                        } else {
                            await exitModal(false);
                        }
                    },
                },
                {
                    text: 'Edit Back of Coin >',
                    class: 'btn btn-secondary text-white btn-modal-to-back',
                    click: modalBtnToBack,
                },
                {
                    text: '< Edit Front of Coin',
                    class: 'btn btn-outline-secondary btn-modal-to-front d-none',
                    click: modalBtnToFront,
                },
            ],
            onClose: () => exitModal(true),
        });

        // The button constructor in the modal was treating the FontAwesome tags as literal,
        // so this is just resetting it to get it to show correctly.
        // The buttons have plain text carets < and > as placeholders by default, before this runs.

        const buttonToBack = activeModal.Element.querySelector('.btn-modal-to-back') as HTMLButtonElement;
        buttonToBack.innerHTML = 'Edit Back of Coin <i class="fas fa-chevron-right"></i>';

        const buttonToFront = activeModal.Element.querySelector('.btn-modal-to-front') as HTMLButtonElement;
        buttonToFront.innerHTML = '<i class="fas fa-chevron-left"></i> Front of Coin';

        // Remove the modal confirm button so we can use ours that checks for uploads before closing
        activeModal.Element.querySelector('#modal-confirm-btn')?.remove();
        activeModal.Element.querySelector('.btn-modal-complete')?.classList.add('d-none');

        const $modalHeader = activeModal.Element.querySelector('.modal-header') as HTMLDivElement;
        $modalHeader.prepend(template.Clone('#modal-sm-back-button'));
        const $modalToFrontSM = document.querySelector('.btn-back') as HTMLButtonElement;

        $modalToFrontSM.addEventListener('click', modalBtnToFront);

        const modalTitle = activeModal.Element.querySelector('.modal-title') as HTMLHeadingElement;

        modalToFront();
    }

    $personalize.addEventListener('click', e => {
        e.preventDefault();
        modalEditor();
    });

    function initPreview() {
        $modalMainRow.classList.remove('d-none');
        $preview?.classList.add('d-none');
        createCoinPreview();
        $modalMainRow.classList.add('d-none');
        $preview?.classList.remove('d-none');
    }

    $reopenEditor.addEventListener('click', e => {
        e.preventDefault();
        modalEditor();
    });

    interface textField {
        name: string;
        value: string;
    }

    function textFieldCartParams(): textField[] {
        const fields: textField[] = [];
        if ($frontTopText.value !== '') {
            fields.push({
                name: 'Front Top Text',
                value: $frontTopText.value,
            });
        }
        if ($frontBottomText.value !== '') {
            fields.push({
                name: 'Front Bottom Text',
                value: $frontBottomText.value,
            });
        }
        if ($backTopText.value !== '') {
            fields.push({
                name: 'Back Top Text',
                value: $backTopText.value,
            });
        }
        if ($backBottomText.value !== '') {
            fields.push({
                name: 'Back Bottom Text',
                value: $backBottomText.value,
            });
        }
        return fields;
    }

    $form.noValidate = true;
    $form.addEventListener('submit', async e => {
        e.preventDefault();

        if (!frontUploadID || !backUploadID) {
            for (const $el of Array.from($uploadRequired)) {
                $el.classList.remove('d-none');
            }
            return;
        }

        if (!ReportValidity($form)) {
            return;
        }

        $fieldset.disabled = true;
        buttons.Start($addToCart);

        // clean up empty inputs
        if (!$frontTopText.value) {
            $frontTopText.dispatchEvent(new Event('input'));
        }
        if (!$frontBottomText.value) {
            $frontBottomText.dispatchEvent(new Event('input'));
        }

        if (!$backTopText.value) {
            $backTopText.dispatchEvent(new Event('input'));
        }
        if (!$backBottomText.value) {
            $backBottomText.dispatchEvent(new Event('input'));
        }

        bgLayer.hide();

        const frontURL = stage.toDataURL({
            quality: 1,
            mimeType: 'image/png',
            x: (offsetX + 72) * scale,
            y: (offsetY + 72) * scale,
            width: 824 * scale,
            height: 824 * scale,
            pixelRatio: 1200 / (824 * scale),
        });

        const backURL = stage.toDataURL({
            quality: 1,
            mimeType: 'image/png',
            x: (offsetX + 72 + 969 + 64) * scale,
            y: (offsetY + 72) * scale,
            width: 824 * scale,
            height: 824 * scale,
            pixelRatio: 1200 / (824 * scale),
        });

        bgLayer.show();

        const previewUpload = await uploads.NewFromURL([{
            name: 'preview.png',
            mime: 'image/png',
            url: frontPreviewImgBlob,
        }]);

        const stageUploads = await uploads.NewFromURL([
            {
                name: '.front.jpg',
                mime: 'image/jpeg',
                url: await stockitems.ColorLayer(frontURL),
            },
            {
                name: '.front-white.jpg',
                mime: 'image/jpeg',
                url: await stockitems.WhiteLayer(frontURL),
            },
            {
                name: '.back.jpg',
                mime: 'image/jpeg',
                url: await stockitems.ColorLayer(backURL),
            },
            {
                name: '.back-white.jpg',
                mime: 'image/jpeg',
                url: await stockitems.WhiteLayer(backURL),
            },
        ]);

        const params: AddCartItemParamsUpload = {
            uploadID: previewUpload[0].uploadID,
            quantity: parseInt($quantity.value, 10),
            options: textFieldCartParams(),
            cartItemUploadIDs: [frontUploadID, backUploadID, ...stageUploads.map(u => u.uploadID)],
            uploadLineItem: {
                name: 'Custom Challenge Coins',
                lineItem: 'challenge coins',
                type: 'Made in USA - Simulated Enamel Coin',
                size: '2"',
                childLineItems: [
                    {
                        child: 'plating',
                        option: 'antique brass',
                    },
                    {
                        child: 'edge',
                        option: 'rope',
                    },
                ],
            },
        };

        AddCartItems(params).then(() => {
            window.location.href = Relref('/cart');
        }).catch(err => {
            $fieldset.disabled = false;
            buttons.Stop($addToCart);
            HandleRequestError(err);
        });
    });

    fitStageIntoParentContainer();
    window.addEventListener('resize', () => fitStageIntoParentContainer(false));

    void Promise.all([addImageFront(placeholderImg), addImageBack(placeholderImg), frontTopPromise, frontBottomPromise, backTopPromise, backBottomPromise]).then(() => {
        stopTransformModeFront();
        stopTransformModeBack();
        initPreview();
        $preview.querySelector('#preview-loading-icon')?.remove();
    });
}